import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { OpeningHoursPeriod } from '../../api/places.api';
import { useProposalPhotos } from '../../hooks/useProposalPhotos';
import SearchPhotoCarousel from '../map/SearchPhotoCarousel';
import { formatWeeklyHours } from '../../utils/openingHours';
import { useDateLocale } from '../../utils/dateLocale';
import { Sparkles, Check, Star, ChevronDown, Clock, Phone, Globe, Luggage, Lightbulb, Wallet, Target, Trash2, MapPin, ChevronRight, Camera, Pencil, Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import type {
  VenueProposal, VenueDeleteProposal, VenueNotesProposal, ActivityProposal, ActivityMoveProposal,
  DayPlanProposal, ReorderProposal, DeleteProposal,
  PackingProposal, TipsProposal, ExpenseProposal, ExpenseDeleteProposal, BudgetProposal,
  ActivityUpdateProposal, PackingItemsProposal, NoteProposal, MealProposal, GroceryProposal,
  EntityUpdateProposal, EntityDeleteProposal, OperationProposal,
} from '../../api/ai.api';
import type { TicketProposal } from '../../api/aiTripProposals';

// Review cards for the AI proposals shown in AiChatPanel. Presentational only:
// each renders one pending proposal and calls onAccept/onDiscard. Extracted from
// AiChatPanel to keep that component focused on the conversation itself.

// ─── Pending UI state (proposals returned by the AI) ──────────────────────

export type Pending =
  | { kind: 'venue';         id: string; status: 'idle' | 'saving' | 'discarded'; data: VenueProposal }
  | { kind: 'venue_delete';  id: string; status: 'idle' | 'saving' | 'discarded'; data: VenueDeleteProposal }
  | { kind: 'venue_notes';   id: string; status: 'idle' | 'saving' | 'discarded'; data: VenueNotesProposal }
  | { kind: 'activity';      id: string; status: 'idle' | 'saving' | 'discarded'; data: ActivityProposal }
  | { kind: 'activity_move'; id: string; status: 'idle' | 'saving' | 'discarded'; data: ActivityMoveProposal }
  | { kind: 'day_plan';      id: string; status: 'idle' | 'saving' | 'discarded'; data: DayPlanProposal }
  | { kind: 'reorder';       id: string; status: 'idle' | 'saving' | 'discarded'; data: ReorderProposal }
  | { kind: 'delete';        id: string; status: 'idle' | 'saving' | 'discarded'; data: DeleteProposal }
  | { kind: 'packing';       id: string; status: 'idle' | 'saving' | 'discarded'; data: PackingProposal }
  | { kind: 'tips';          id: string; status: 'idle' | 'saving' | 'discarded'; data: TipsProposal }
  | { kind: 'expense';        id: string; status: 'idle' | 'saving' | 'discarded'; data: ExpenseProposal }
  | { kind: 'expense_delete'; id: string; status: 'idle' | 'saving' | 'discarded'; data: ExpenseDeleteProposal }
  | { kind: 'budget';         id: string; status: 'idle' | 'saving' | 'discarded'; data: BudgetProposal }
  | { kind: 'activity_update'; id: string; status: 'idle' | 'saving' | 'discarded'; data: ActivityUpdateProposal }
  | { kind: 'packing_items';  id: string; status: 'idle' | 'saving' | 'discarded'; data: PackingItemsProposal }
  | { kind: 'note';           id: string; status: 'idle' | 'saving' | 'discarded'; data: NoteProposal }
  | { kind: 'ticket';         id: string; status: 'idle' | 'saving' | 'discarded'; data: TicketProposal }
  | { kind: 'meal';           id: string; status: 'idle' | 'saving' | 'discarded'; data: MealProposal }
  | { kind: 'grocery';        id: string; status: 'idle' | 'saving' | 'discarded'; data: GroceryProposal }
  | { kind: 'entity_update';  id: string; status: 'idle' | 'saving' | 'discarded'; data: EntityUpdateProposal }
  | { kind: 'entity_delete';  id: string; status: 'idle' | 'saving' | 'discarded'; data: EntityDeleteProposal }
  | { kind: 'operation';      id: string; status: 'idle' | 'saving' | 'discarded'; data: OperationProposal };

// The rich place fields a card renders. A saved-place proposal and the new
// place an activity schedules carry the same ones — they come from the same
// Place Details call — but with different nullability, so the card reads them
// through this shape instead of casting one into the other.
type ProposalPlace = {
  notes?:            string | null;
  address?:          string | null;
  mapsUrl?:          string | null;
  phone?:            string | null;
  website?:          string | null;
  rating?:           number | null;
  userRatingsTotal?: number | null;
  priceLevel?:       number | null;
  openNow?:          boolean | null;
  openingHoursPeriods?: OpeningHoursPeriod[] | null;
  photoUrl?:         string | null;
  photoUrls?:        string[] | null;
};

// ─── Proposal card ─────────────────────────────────────────────────────────

function formatReviewCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

// Marks an activity whose place isn't saved yet: accepting the proposal creates
// it in Places and schedules it in one go. Shown on activity/day-plan cards.
function SaveToPlacesPill() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">
      <Icon as={MapPin} size={9} />
      {t('ai.willSaveToPlaces')}
    </span>
  );
}

export function ProposalCard({ pending, tripId, onAccept, onDiscard }: {
  pending: Pending;
  tripId?: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const isSaving = pending.status === 'saving';
  const [expanded, setExpanded] = useState(false);

  let title = '';
  let subtitle = '';
  let venueData: ProposalPlace | null = null;
  if (pending.kind === 'venue') {
    title = pending.data.name;
    const cat = t(`ai.category.${pending.data.category}`) ?? pending.data.category;
    subtitle = [cat, pending.data.zone].filter(Boolean).join(' · ');
    venueData = pending.data;
  } else if (pending.kind === 'activity') {
    title = pending.data.title;
    subtitle = [
      pending.data.venueName,
      pending.data.zone,
      // Where it lands, when the model asked for a slot. The assistant says it
      // in prose either way; without it on the card, "between these two" is a
      // claim the user can only check after accepting.
      pending.data.afterActivityTitle
        ? t('ai.afterStop', { title: pending.data.afterActivityTitle })
        : null,
    ].filter(Boolean).join(' · ');
    // The place an activity schedules carries the same rich fields as a venue
    // proposal — already paid for by the same Place Details call — so the card
    // shows them instead of dropping them on the floor.
    venueData = pending.data.newVenue ?? null;
  } else if (pending.kind === 'activity_move') {
    title = t('ai.move', { title: pending.data.title });
  }

  // Built from Google's numeric periods rather than its `weekdayDescriptions`
  // prose, which arrives in Google's own language and read "Monday 10:00 AM"
  // inside an otherwise Spanish card. Same helper the venue card uses.
  const weekHours = venueData?.openingHoursPeriods?.length
    ? formatWeeklyHours(venueData.openingHoursPeriods, dateLocale)
    : null;

  const hasExtra = !!venueData && (
    !!venueData.notes ||
    !!weekHours ||
    !!venueData.phone ||
    !!venueData.website ||
    !!venueData.mapsUrl ||
    !!venueData.address
  );

  // Free on mount, paid on request — see useProposalPhotos and ADR-0005.
  const allPhotos = venueData?.photoUrls?.filter((p): p is string => !!p)
    ?? (venueData?.photoUrl ? [venueData.photoUrl] : []);
  const { urls, revealing, hiddenCount, reveal } = useProposalPhotos(allPhotos, tripId);
  const shown = allPhotos.filter(p => p in urls).map(p => urls[p]);
  const photoSrc = shown[0] ?? null;

  return (
    <div className="rounded-xl border border-teal-200 dark:border-teal-900/60 bg-teal-50/60 dark:bg-teal-950/30 p-3">
      {/* The thumbnail sits OUTSIDE the expand button: nesting the "show photos"
          control inside it made it unreachable on a card with nothing to expand,
          because that button is disabled and swallows clicks on its children. */}
      <div className="flex items-start gap-2.5 mb-2.5">
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={title}
            className="w-14 h-14 rounded-lg object-cover shrink-0 border border-teal-200 dark:border-teal-900 bg-slate-100 dark:bg-slate-800"
            loading="lazy"
          />
        ) : hiddenCount > 0 ? (
          <button
            type="button"
            onClick={reveal}
            disabled={revealing}
            aria-label={t('ai.showPhotos')}
            title={t('ai.showPhotos')}
            className="w-14 h-14 rounded-lg shrink-0 border border-dashed border-teal-300 dark:border-teal-800 bg-white/60 dark:bg-slate-800/60 flex items-center justify-center cursor-pointer hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
          >
            {revealing ? <Spinner size="sm" /> : <Icon as={Camera} size={15} className="text-teal-500 dark:text-teal-400" />}
          </button>
        ) : (
          <div className="w-6 h-6 rounded-md bg-white dark:bg-slate-800 border border-teal-200 dark:border-teal-900 flex items-center justify-center shrink-0 mt-0.5">
            <Icon as={Sparkles} size={12} className="text-teal-500 dark:text-teal-400" />
          </div>
        )}
        <button
          type="button"
          onClick={() => hasExtra && setExpanded(e => !e)}
          disabled={!hasExtra}
          className={`flex-1 min-w-0 text-left ${hasExtra ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <div className="flex items-start gap-1.5">
            <p className="flex-1 text-[13px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">{title}</p>
            {hasExtra && (
              <Icon as={ChevronDown} size={14} className={`text-slate-400 dark:text-slate-500 shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            )}
          </div>
          {subtitle && <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{subtitle}</p>}
          {pending.kind === 'activity' && pending.data.newVenue && <div className="mt-1"><SaveToPlacesPill /></div>}
          {venueData && (venueData.rating != null || venueData.priceLevel != null || venueData.openNow != null) && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
              {venueData.rating != null && (
                <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                  <Icon as={Star} size={11} className="fill-amber-500 text-amber-500" />
                  {venueData.rating.toFixed(1)}
                  {venueData.userRatingsTotal != null && (
                    <span className="text-slate-400 dark:text-slate-500 font-normal">
                      ({formatReviewCount(venueData.userRatingsTotal)})
                    </span>
                  )}
                </span>
              )}
              {venueData.priceLevel != null && venueData.priceLevel > 0 && (
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                  {'€'.repeat(Math.min(venueData.priceLevel, 4))}
                </span>
              )}
              {venueData.openNow != null && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  venueData.openNow
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                }`}>
                  {venueData.openNow ? t('ai.open') : t('ai.closed')}
                </span>
              )}
            </div>
          )}
          {!expanded && venueData?.address && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 leading-snug truncate">{venueData.address}</p>
          )}
        </button>
      </div>

      {expanded && venueData && (
        <div className="mb-2.5 pt-2 border-t border-teal-200/60 dark:border-teal-900/60 space-y-2">
          {/* Opening one card is a user gesture bounded to one place, so the
              carousel may resolve as it goes — it only ever loads the photo on
              screen (ADR-0001). */}
          {shown.length > 0 && (
            <div className="rounded-lg overflow-hidden -mx-0.5">
              <SearchPhotoCarousel photos={allPhotos} name={title} tripId={tripId} />
            </div>
          )}
          {venueData.notes && (
            <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed break-words">{venueData.notes}</p>
          )}
          {venueData.address && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">{venueData.address}</p>
          )}
          {weekHours && (
            <div className="flex items-start gap-1.5">
              <Icon as={Clock} size={12} className="text-slate-400 dark:text-slate-500 shrink-0 mt-0.5" />
              <div className="flex-1 text-[10.5px] text-slate-500 dark:text-slate-400 leading-relaxed space-y-0.5">
                {weekHours.map(({ day, hours }) => (
                  <p key={day} className="capitalize">{day}: {hours ?? t('venues.closedDay')}</p>
                ))}
              </div>
            </div>
          )}
          {(venueData.phone || venueData.website || venueData.mapsUrl) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
              {venueData.phone && (
                <a href={`tel:${venueData.phone}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-600 dark:text-teal-400 hover:underline">
                  <Icon as={Phone} size={11} />
                  {venueData.phone}
                </a>
              )}
              {venueData.website && (
                <a href={venueData.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-600 dark:text-teal-400 hover:underline">
                  <Icon as={Globe} size={11} />
                  Web
                </a>
              )}
              {venueData.mapsUrl && (
                <a href={venueData.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-600 dark:text-teal-400 hover:underline">
                  <Icon as={Globe} size={11} />
                  Google Maps
                </a>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Day plan card ──────────────────────────────────────────────────────────

export function DayPlanCard({ pending, tripId, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'day_plan' }>;
  tripId?: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { dayNumber, date, activities } = pending.data;

  // One photo per stop, first-listed only: a day is exactly the kind of card
  // whose cost would otherwise scale with how much it shows (ADR-0005), so the
  // cached ones paint for free and the rest wait for the button in the header.
  const stopPhotos = activities.map(a => a.newVenue?.photoUrl ?? null);
  const { urls, revealing, hiddenCount, reveal } = useProposalPhotos(
    stopPhotos.filter((p): p is string => !!p), tripId,
  );
  const dateLabel = date ? new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '';

  return (
    <div className="rounded-xl border border-violet-200 dark:border-violet-900/60 bg-violet-50/60 dark:bg-violet-950/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-violet-200/70 dark:border-violet-900/50 flex items-center gap-2">
        <span className="text-xs font-bold text-violet-700 dark:text-violet-300">
          {dayNumber != null ? t('ai.dayNumber', { number: dayNumber }) : t('ai.dayPlanTitle')}
          {dateLabel && <span className="font-normal text-violet-500 dark:text-violet-400 ml-1">· {dateLabel}</span>}
        </span>
        <span className="ml-auto text-[10px] text-violet-400 dark:text-violet-500">{t('ai.activitiesCount', { count: activities.length })}</span>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={reveal}
            disabled={revealing}
            className="shrink-0 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors cursor-pointer disabled:opacity-50"
          >
            {revealing ? <Spinner size="sm" /> : <Icon as={Camera} size={11} />}
            {t('ai.showPhotos')}
          </button>
        )}
      </div>
      <div className="px-3 py-2 space-y-1.5 max-h-56 overflow-y-auto">
        {activities.map((act, i) => {
          const photo = stopPhotos[i] ? urls[stopPhotos[i]!] : undefined;
          return (
          <div key={i} className="flex items-start gap-2">
            {photo ? (
              <img
                src={photo}
                alt={act.title}
                className="w-9 h-9 rounded-md object-cover shrink-0 border border-violet-200 dark:border-violet-900 bg-slate-100 dark:bg-slate-800"
                loading="lazy"
              />
            ) : (
              <span className="text-base leading-none mt-0.5 shrink-0">📍</span>
            )}
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
                {act.startTime && <span className="text-violet-600 dark:text-violet-400 font-mono mr-1.5">{act.startTime}</span>}
                {act.title}
              </p>
              {(act.venueName || act.zone) && (
                <p className="text-[10.5px] text-slate-400 dark:text-slate-500 leading-snug">
                  {[act.venueName, act.zone].filter(Boolean).join(' · ')}
                </p>
              )}
              {act.newVenue && <SaveToPlacesPill />}
              {act.notes && (
                <p className="text-[10.5px] text-slate-500 dark:text-slate-400 italic leading-snug mt-0.5 break-words">{act.notes}</p>
              )}
            </div>
          </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-violet-200/70 dark:border-violet-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.acceptDay')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-violet-200 dark:border-violet-800 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Reorder card ───────────────────────────────────────────────────────────

export function ReorderCard({ pending, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'reorder' }>;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { dayNumber, activities } = pending.data;

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-amber-200/70 dark:border-amber-900/50">
        <span className="text-xs font-bold text-amber-700 dark:text-amber-300">
          🔀 {t('ai.reorderTitle')}{dayNumber != null ? ` · ${t('ai.dayNumber', { number: dayNumber })}` : ''}
        </span>
      </div>
      <div className="px-3 py-2 space-y-1">
        {activities.map((act, i) => (
          <div key={act.id} className="flex items-center gap-2">
            <span className="w-4 text-[10px] font-bold text-amber-500 dark:text-amber-400 text-right shrink-0">{i + 1}.</span>
            <p className="text-[12px] text-slate-700 dark:text-slate-200 leading-snug">{act.title}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-amber-200/70 dark:border-amber-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.applying') : t('ai.applyOrder')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Delete card ────────────────────────────────────────────────────────────

export function DeleteCard({ pending, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'delete' }>;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';

  return (
    <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3">
      <div className="flex items-start gap-2.5 mb-2.5">
        <div className="w-6 h-6 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0 mt-0.5">
          <Icon as={Trash2} size={12} className="text-red-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">{t('ai.deleteActivity')}</p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{pending.data.title}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Trash2} size={12} />}
          {isSaving ? t('common.deleting') : t('common.confirm')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Venue delete card ──────────────────────────────────────────────────────
// One card per delete request, however many places it covers: "empty the whole
// list" is a single confirmation, not 36 of them.

export function VenueDeleteCard({ pending, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'venue_delete' }>;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { venues, all, removedActivities } = pending.data;

  return (
    <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3">
      <div className="flex items-start gap-2.5 mb-2.5">
        <div className="w-6 h-6 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0 mt-0.5">
          <Icon as={Trash2} size={12} className="text-red-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
            {all ? t('ai.deleteAllPlaces', { count: venues.length }) : t('ai.deletePlaces', { count: venues.length })}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-3">
            {venues.map(v => v.name).join(' · ')}
          </p>
          {/* Deleting a place takes its stops with it, so "empty the list" can
              empty half the itinerary. The number goes before the button, not
              in the toast afterwards. */}
          {!!removedActivities && (
            <p className="text-[11px] font-semibold text-red-600 dark:text-red-400 mt-1 leading-snug">
              {t('ai.deleteTakesStops', { count: removedActivities })}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Trash2} size={12} />}
          {isSaving ? t('common.deleting') : t('common.confirm')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Venue notes card ───────────────────────────────────────────────────────

/**
 * New notes for a saved place, shown as what they are: a replacement.
 *
 * Both texts, not just the result. The user is being asked whether to throw
 * away something they wrote, and the only thing that answers that is seeing
 * what goes. A place with no notes yet skips the "before" half — there is
 * nothing being replaced, so a struck-through empty box would be noise.
 */
export function VenueNotesCard({ pending, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'venue_notes' }>;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { venueName, before, after } = pending.data;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
      <div className="flex items-start gap-2.5 mb-2.5">
        <div className="w-6 h-6 rounded-md bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0 mt-0.5">
          <Icon as={Pencil} size={12} className="text-teal-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
            {before ? t('ai.venueNotes.title', { name: venueName }) : t('ai.venueNotes.titleNew', { name: venueName })}
          </p>
        </div>
      </div>

      <div className="space-y-1.5 mb-2.5">
        {before && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-2.5 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">
              {t('ai.venueNotes.before')}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 line-through leading-snug whitespace-pre-wrap">
              {before}
            </p>
          </div>
        )}
        <div className="rounded-lg bg-teal-50/70 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-900/40 px-2.5 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 dark:text-teal-400 mb-0.5">
            {t('ai.venueNotes.after')}
          </p>
          <p className="text-[11px] text-slate-700 dark:text-slate-200 leading-snug whitespace-pre-wrap">
            {after || t('ai.venueNotes.emptied')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={12} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Packing card ───────────────────────────────────────────────────────────

export function PackingCard({ pending, tripId, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'packing' }>;
  tripId?: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSaving = pending.status === 'saving';
  const { sections } = pending.data;
  const itemCount = sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900/60 bg-sky-50/60 dark:bg-sky-950/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-sky-200/70 dark:border-sky-900/50 flex items-center gap-2">
        <Icon as={Luggage} size={14} className="text-sky-600 dark:text-sky-400" />
        <span className="text-xs font-bold text-sky-700 dark:text-sky-300">{t('ai.packingTitle')}</span>
        <span className="ml-auto text-[10px] text-sky-400 dark:text-sky-500">{t('ai.packingItems', { count: itemCount })}</span>
      </div>
      <div className="px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
        {sections.map((sec, i) => (
          <div key={i}>
            <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 leading-snug">{sec.title}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {sec.items.map((it, j) => (
                <span key={j} className="text-[10.5px] px-1.5 py-0.5 rounded-md bg-white dark:bg-slate-800 border border-sky-200/70 dark:border-sky-900/50 text-slate-600 dark:text-slate-300">{it}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-sky-200/70 dark:border-sky-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-sky-200 dark:border-sky-800 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
      {tripId && (
        <button
          onClick={() => navigate(`/trips/${tripId}/packing`)}
          className="w-full px-3 py-1.5 border-t border-sky-200/70 dark:border-sky-900/50 flex items-center justify-center gap-1 text-[11px] font-medium text-sky-600 dark:text-sky-400 hover:bg-sky-100/60 dark:hover:bg-sky-900/20 transition-colors cursor-pointer"
        >
          {t('ai.packingGoToTab')}
          <Icon as={ChevronRight} size={12} />
        </button>
      )}
    </div>
  );
}

// ─── Budget card ──────────────────────────────────────────────────────────────

export function BudgetCard({ pending, baseCurrency, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'budget' }>;
  baseCurrency: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { limits } = pending.data;

  const labelFor = (l: BudgetProposal['limits'][number]): string => {
    if (l.scope === 'TOTAL') return t('expenses.budget.total');
    if (l.scope === 'DAILY') return t('expenses.budget.daily');
    return l.category
      ? t(`expenses.categories.${l.category}` as Parameters<typeof t>[0])
      : t('expenses.budget.title');
  };

  return (
    <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-emerald-200/70 dark:border-emerald-900/50 flex items-center gap-2">
        <Icon as={Target} size={14} className="text-emerald-600 dark:text-emerald-400" />
        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">{t('ai.budgetTitle')}</span>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {limits.map((l, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-slate-700 dark:text-slate-200 leading-snug truncate">{labelFor(l)}</span>
            <span className="text-[12px] font-bold text-emerald-700 dark:text-emerald-300 shrink-0 tabular-nums">
              {l.amount.toFixed(0)} {baseCurrency}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-emerald-200/70 dark:border-emerald-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Expense card ─────────────────────────────────────────────────────────────

export function ExpenseCard({ pending, baseCurrency, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'expense' }>;
  baseCurrency: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { description, amount, category, notes } = pending.data;
  const categoryLabel = t(`expenses.categories.${category}` as Parameters<typeof t>[0]) || category;

  return (
    <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/20 overflow-hidden">
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
          <Icon as={Wallet} size={15} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 leading-snug truncate">{description}</p>
            <span className="text-[13px] font-bold text-emerald-700 dark:text-emerald-300 shrink-0 tabular-nums">
              {amount.toFixed(2)} {baseCurrency}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{categoryLabel}</p>
          {notes && <p className="text-[10.5px] text-slate-500 dark:text-slate-400 italic leading-snug mt-1">{notes}</p>}
          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 leading-snug">{t('ai.expenseSplitHint')}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-emerald-200/70 dark:border-emerald-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}

// ─── Expense delete card ────────────────────────────────────────────────────
// One card per delete request, however many expenses it covers: "borra todos los
// gastos" is a single confirmation. Shows the total at stake — the number the
// user actually checks before wiping a ledger.

export function ExpenseDeleteCard({ pending, baseCurrency, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'expense_delete' }>;
  baseCurrency: string;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { expenses, all } = pending.data;
  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3">
      <div className="flex items-start gap-2.5 mb-2.5">
        <div className="w-6 h-6 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0 mt-0.5">
          <Icon as={Trash2} size={12} className="text-red-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
              {all ? t('ai.deleteAllExpenses', { count: expenses.length }) : t('ai.deleteExpenses', { count: expenses.length })}
            </p>
            <span className="text-[12px] font-bold text-red-600 dark:text-red-400 shrink-0 tabular-nums">
              −{total.toFixed(2)} {baseCurrency}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-3">
            {expenses.map(e => e.description).join(' · ')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Trash2} size={12} />}
          {isSaving ? t('common.deleting') : t('common.confirm')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Tips card ──────────────────────────────────────────────────────────────

export function TipsCard({ pending, onAccept, onDiscard }: {
  pending: Extract<Pending, { kind: 'tips' }>;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const isSaving = pending.status === 'saving';
  const { tips } = pending.data;

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-amber-200/70 dark:border-amber-900/50 flex items-center gap-2">
        <Icon as={Lightbulb} size={14} className="text-amber-600 dark:text-amber-400" />
        <span className="text-xs font-bold text-amber-700 dark:text-amber-300">{t('ai.tipsTitle')}</span>
        <span className="ml-auto text-[10px] text-amber-400 dark:text-amber-500">{t('ai.tipsCount', { count: tips.length })}</span>
      </div>
      <div className="px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
        {tips.map((tip, i) => (
          <div key={i}>
            <p className="text-[9.5px] font-semibold uppercase tracking-wide text-amber-500 dark:text-amber-400">{tip.category}</p>
            <p className="text-[11.5px] text-slate-600 dark:text-slate-300 leading-snug">{tip.content}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-amber-200/70 dark:border-amber-900/50">
        <button
          onClick={onAccept}
          disabled={isSaving}
          className="flex-1 h-8 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
        >
          {isSaving ? <Spinner size="sm" /> : <Icon as={Check} size={13} />}
          {isSaving ? t('ai.saving') : t('ai.accept')}
        </button>
        <button
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 px-3 rounded-lg border border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/20 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('ai.discard')}
        </button>
      </div>
    </div>
  );
}
